/**
 * Section Component
 *
 * A responsive section component that handles consistent margin and padding
 * based on screen size breakpoints.
 */
import React from 'react';

import { cn } from '@ui/lib/utils';

export interface SectionProps {
  /**
   * Content to be rendered inside the section
   */
  children: React.ReactNode;

  /**
   * Whether the section should extend to the full width of the viewport
   * This will apply negative margins to break out of the container
   */
  fullWidth?: boolean;

  /**
   * Additional CSS classes to apply to the section element
   */
  className?: string;

  /**
   * Set to true to disable the default vertical padding (py-15 / 60px).
   * Defaults to false (padding is applied).
   */
  disableVerticalPadding?: boolean;
}

/**
 * Section component for consistent layout across the application.
 *
 * Applies responsive margins and max-width directly to the section element
 * when `fullWidth` is false, based on custom screen breakpoints:
 * - Default: 20px horizontal margin (`mx-5`)
 * - sm (800px+): 40px horizontal margin (`sm:mx-10`)
 * - md (1024px+): 80px horizontal margin (`md:mx-20`)
 * - lg (1360px+): Centered with 1200px max-width (`lg:max-w-7xl lg:mx-auto`)
 *
 * The `fullWidth` prop removes these constraints, allowing the section
 * to span the full viewport width.
 */
export const Section: React.FC<SectionProps> = ({
  children,
  fullWidth = false,
  className,
  disableVerticalPadding = false // Default to false (padding applied)
}) => {
  return (
    <section className={cn('@container', !disableVerticalPadding && 'py-15', !fullWidth && 'layout-bounds', className)}>
      {children}
    </section>
  );
};
